import { useEffect, useState } from 'react'
import { fetchPostureThresholds, updatePostureThresholds } from '@/api/posture'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  clearPostureThresholdsCache,
  DEFAULT_POSTURE_THRESHOLDS,
  mergePostureThresholds,
} from '@/lib/postureThresholds'

const FIELDS = [
  {
    group: 'Memory usage (Toolkit % used)',
    items: [
      {
        key: 'toolkit_memory_warn_used_pct',
        label: 'Review at or above (% used)',
        hint: 'Bekat Toolkit Memory Usage check. Default 80%',
      },
      {
        key: 'toolkit_memory_fail_used_pct',
        label: 'Action needed at or above (% used)',
        hint: 'Default 90%. Example: 94% used fails at 90, passes if you raise this above 94',
      },
    ],
  },
  {
    group: 'Installed RAM (capacity)',
    items: [
      {
        key: 'workstation_ram_red_below_mb',
        label: 'Workstation — action needed below (MB)',
        hint: 'Installed RAM capacity from Atera, not live usage %. Default 4096 (~4 GB)',
      },
      {
        key: 'workstation_ram_amber_below_mb',
        label: 'Workstation — review below (MB)',
        hint: 'Default 7800 (~8 GB usable floor)',
      },
      {
        key: 'server_ram_red_below_mb',
        label: 'Server — action needed below (MB)',
        hint: 'Installed RAM capacity from Atera. Default 7800 (~8 GB)',
      },
      {
        key: 'server_ram_amber_below_mb',
        label: 'Server — review below (MB)',
        hint: 'Default 15872 (~16 GB)',
      },
    ],
  },
  {
    group: 'Disk free space',
    items: [
      { key: 'disk_low_pct', label: 'Review below (% free)', hint: 'Default 10%' },
      { key: 'disk_low_gb', label: 'Review below (GB free)', hint: 'Default 5 GB' },
      { key: 'disk_critical_pct', label: 'Action needed below (% free)', hint: 'Default 5%' },
      { key: 'disk_critical_gb', label: 'Action needed below (GB free)', hint: 'Default 2 GB' },
      {
        key: 'small_drive_below_gb',
        label: 'Small drive review below (GB total)',
        hint: 'Default 128 GB',
      },
    ],
  },
  {
    group: 'Processor',
    items: [
      { key: 'cpu_cores_red_below', label: 'Strong review below (cores)', hint: 'Default 2 — CPU never escalates to Action needed' },
      { key: 'cpu_cores_amber_below', label: 'Review below (cores)', hint: 'Default 4' },
      {
        key: 'cpu_speed_amber_below_mhz',
        label: 'Review below (CPU MHz)',
        hint: 'Default 2000 (2.0 GHz) — always Review, never Action needed',
      },
    ],
  },
  {
    group: 'Firmware',
    items: [
      {
        key: 'bios_age_amber_years',
        label: 'Review BIOS older than (years)',
        hint: 'Default 4 years — raise this to cut firmware noise',
      },
    ],
  },
]

function valuesEqual(a, b) {
  return FIELDS.flatMap((g) => g.items).every(
    (item) => Number(a[item.key]) === Number(b[item.key]),
  )
}

export function PostureThresholdsSettingsCard({ embedded = false }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [saved, setSaved] = useState({ ...DEFAULT_POSTURE_THRESHOLDS })
  const [draft, setDraft] = useState({ ...DEFAULT_POSTURE_THRESHOLDS })

  useEffect(() => {
    fetchPostureThresholds()
      .then((data) => {
        const merged = mergePostureThresholds(data)
        setSaved(merged)
        setDraft(merged)
      })
      .catch(() => setError('Could not load posture thresholds.'))
      .finally(() => setLoading(false))
  }, [])

  const hasUnsavedChanges = !valuesEqual(draft, saved)

  function setField(key, raw) {
    setDraft((prev) => ({ ...prev, [key]: raw }))
  }

  async function handleSave(event) {
    event.preventDefault()
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const body = {}
      for (const item of FIELDS.flatMap((g) => g.items)) {
        body[item.key] = Number(draft[item.key])
      }
      const updated = mergePostureThresholds(await updatePostureThresholds(body))
      setSaved(updated)
      setDraft(updated)
      clearPostureThresholdsCache()
      setMessage('Posture thresholds saved. Device lists and dashboards will use the new values.')
    } catch (err) {
      const detail = err?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Could not save posture thresholds.')
    } finally {
      setSaving(false)
    }
  }

  function handleResetDefaults() {
    setDraft({ ...DEFAULT_POSTURE_THRESHOLDS })
  }

  if (loading) {
    return <Skeleton className="h-64 w-full" />
  }

  return (
    <div className="space-y-4">
      {!embedded ? (
        <Card>
          <CardHeader>
            <CardTitle>Posture thresholds</CardTitle>
            <CardDescription>
              Tune when devices show Review or Action needed for hardware attention.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      <form onSubmit={handleSave} className="space-y-6">
        {embedded ? (
          <p className="text-sm text-muted-foreground">
            These values drive Hardware posture, Toolkit Memory Usage results on device pages,
            estate lists, and dashboard attention counts. Memory usage % is re-scored from the
            Bekat Toolkit snapshot using your thresholds (no need to re-run Atera for the portal
            view). Installed RAM floors are separate capacity checks from Atera inventory.
            Security yes/no checks (BitLocker, macros, and so on) are unchanged.
          </p>
        ) : null}

        {FIELDS.map((group) => (
          <div key={group.group} className="space-y-3">
            <h3 className="text-sm font-medium">{group.group}</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              {group.items.map((item) => (
                <div key={item.key} className="space-y-1.5">
                  <Label htmlFor={`posture-${item.key}`}>{item.label}</Label>
                  <Input
                    id={`posture-${item.key}`}
                    type="number"
                    step="any"
                    value={draft[item.key] ?? ''}
                    onChange={(event) => setField(item.key, event.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">{item.hint}</p>
                </div>
              ))}
            </div>
          </div>
        ))}

        {error && <p className="text-sm text-destructive">{error}</p>}
        {message && <p className="text-sm text-emerald-600">{message}</p>}

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={saving || !hasUnsavedChanges}>
            {saving ? 'Saving…' : 'Save posture thresholds'}
          </Button>
          <Button type="button" variant="outline" onClick={handleResetDefaults}>
            Reset form to defaults
          </Button>
        </div>
      </form>
    </div>
  )
}
